import { useReducer } from 'react';
import { createSlice } from '@reduxjs/toolkit';

const initialState = [];

const orderProductsSlice = createSlice({
	name: 'newOrder/products',
	reducers: {
		addProducts(state, { payload: product }) {
			// Ensure we are working with an array to support both single and batch updates.
			const products = Array.isArray(product) ? product : [product];
			const stateIds = new Set(state.map((item) => item.combined_id));

			return [
				...state,
				...products
					// Remove new products that are already in the list.
					// This prevents a bug where the same item is added to the list multiple times with the same ID.
					.filter(({ combined_id }) => !stateIds.has(combined_id))
					.map((newProduct) => {
						// Ensure products have a combined_id
						if (!newProduct.combined_id) {
							newProduct.combined_id = [
								'custom',
								newProduct.partNumber,
								newProduct.year,
								newProduct.make,
								newProduct.model,
								newProduct.subModel,
								newProduct.product_type,
								newProduct.product_name,
								newProduct.color,
							].join('-');
						}

						// Ensure we have a quantity set
						if (Number.isNaN(newProduct.quantity))
							newProduct.quantity = 1;

						return {
							...newProduct,
							questions: newProduct.questions || [],
							addons: newProduct.addons || [],
						};
					}),
			];
		},
		addCustomProducts(state, { payload: product }) {
			const characters =
				'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
			const products = Array.isArray(product) ? product : [product];
			const charactersLength = characters.length;

			return [
				...state,
				...products.map((productPayload) => {
					let randomPart = '';
					for (let i = 0; i < 10; i++) {
						randomPart += characters.charAt(
							Math.floor(Math.random() * charactersLength),
						);
					}
					// Add timestamp to make it more unique
					const combined_id = `custom-${Date.now()}-${randomPart}`;

					const newProduct = { ...productPayload };
					newProduct.combined_id = combined_id;
					newProduct.price = newProduct.priceIncTax;
					newProduct.vendor = newProduct.vendorName;
					newProduct.is_custom_product = true;
					newProduct.in_stock = 'available';
					newProduct.quantity = 1;

					// Map new custom product fields to database columns
					newProduct.name = productPayload.product || '';
					newProduct.color = productPayload.color || '';
					newProduct.part_number = productPayload.partNumber || '';
					newProduct.year = productPayload.year || '';
					newProduct.make = productPayload.make || '';
					newProduct.model = productPayload.model || '';
					newProduct.seat = productPayload.seatDescription || '';

					// Set default values for other required fields
					newProduct.sub_model = '';
					newProduct.row = productPayload.row || '';
					newProduct.diagram = '';
					newProduct.notes = '';
					newProduct.description = '';

					return {
						...newProduct,
						questions: [],
						addons: [],
					};
				}),
			];
		},
		updateProducts(state, { payload: product }) {
			// Ensure we are working with an array to support both single and batch updates.
			const products = Array.isArray(product) ? product : [product];
			// Get a map reference to the products by combined id.
			const productsByCombinedId = new Map(
				products.map((mappedProduct) => [
					mappedProduct.combined_id,
					mappedProduct,
				]),
			);

			return state.map(
				(existingProduct) =>
					productsByCombinedId.get(existingProduct.combined_id) ??
					existingProduct,
			);
		},
		removeProducts(state, { payload: product }) {
			// Ensure we are working with an array to support both single and batch updates.
			const products = Array.isArray(product) ? product : [product];

			// Return the new copy minus any products to remove.
			return state.filter(
				({ combined_id }) => !products.includes(combined_id),
			);
		},
	},
});

export const {
	addProducts,
	addCustomProducts,
	updateProducts,
	removeProducts,
} = orderProductsSlice.actions;

export default function useOrderProductsStore() {
	return useReducer(orderProductsSlice.reducer, initialState);
}
